import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { mockMenus, adminPermissions } from '@/mock/data'
import {
  login as loginApi,
  getProfile as getProfileApi,
  logout as logoutApi,
} from '@/api/auth'

const TOKEN_KEY = 'admin_token'
const SUPPLIER_KEY = 'admin_supplier'
const MENUS_KEY = 'admin_menus'
const PERMS_KEY = 'admin_permissions'

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem(TOKEN_KEY) || '')
  /** 供应商完整信息（后端返回的 supplier / profile 对象） */
  const supplier = ref(JSON.parse(localStorage.getItem(SUPPLIER_KEY) || 'null'))
  const permissions = ref(JSON.parse(localStorage.getItem(PERMS_KEY) || '[]'))
  const menus = ref(JSON.parse(localStorage.getItem(MENUS_KEY) || '[]'))

  const isLoggedIn = computed(() => !!token.value)
  /** 账号信息别名，兼容旧 userInfo 调用 */
  const userInfo = computed(() => supplier.value)
  /** 0=待审核 1=已通过 2=已驳回 */
  const auditStatus = computed(() => supplier.value?.audit_status ?? 0)
  const isAuditPassed = computed(() => auditStatus.value === 1)
  /** 0=未缴 1=已缴 */
  const depositStatus = computed(() => supplier.value?.deposit_status ?? 0)
  /** 0=休息中 1=营业中 */
  const businessStatus = computed(() => supplier.value?.business_status ?? 0)

  function persistSupplier(s) {
    supplier.value = s || null
    if (s) localStorage.setItem(SUPPLIER_KEY, JSON.stringify(s))
    else localStorage.removeItem(SUPPLIER_KEY)
  }

  function setMenusAndPerms(menuList = mockMenus, perms = adminPermissions) {
    menus.value = menuList
    permissions.value = perms
    localStorage.setItem(MENUS_KEY, JSON.stringify(menuList))
    localStorage.setItem(PERMS_KEY, JSON.stringify(perms))
  }

  /**
   * 供应商手机号+验证码登录
   * 后端返回: { token, supplier }
   */
  async function phoneLogin(phone, verify_code) {
    const res = await loginApi(phone, verify_code)
    token.value = res.data.token
    localStorage.setItem(TOKEN_KEY, res.data.token)
    persistSupplier(res.data.supplier)
    // 菜单使用本地固定配置
    setMenusAndPerms(mockMenus, adminPermissions)
    return res.data
  }

  /** 刷新当前供应商信息 */
  async function refreshProfile() {
    if (!token.value) return null
    const res = await getProfileApi()
    persistSupplier(res.data)
    return res.data
  }

  /** 局部更新 supplier 字段（如营业状态、保证金状态变更后） */
  function patchSupplier(patch) {
    if (!supplier.value) return
    persistSupplier({ ...supplier.value, ...patch })
  }

  function clearLocal() {
    token.value = ''
    persistSupplier(null)
    permissions.value = []
    menus.value = []
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(MENUS_KEY)
    localStorage.removeItem(PERMS_KEY)
  }

  /** 退出登录：尝试调后端接口后清除本地 */
  async function logout() {
    try {
      if (token.value) await logoutApi()
    } catch (e) {
      // 忽略退出接口错误，本地仍然清除
    }
    clearLocal()
  }

  function hasPermission(permission) {
    if (!permission) return true
    if (Array.isArray(permission)) return permission.some((p) => permissions.value.includes(p))
    return permissions.value.includes(permission)
  }

  return {
    token,
    supplier,
    userInfo,
    permissions,
    menus,
    isLoggedIn,
    auditStatus,
    isAuditPassed,
    depositStatus,
    businessStatus,
    phoneLogin,
    refreshProfile,
    patchSupplier,
    logout,
    clearLocal,
    hasPermission,
  }
})
